import { motion } from 'motion/react'
import { MessageCircle, Mail, BookOpenText, CircleDot, Settings } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ScreenName } from '../shared/types/navigation'

interface Props {
  activeTab: ScreenName
  onNavigate: (screen: ScreenName) => void
}

export default function BottomNav({ activeTab, onNavigate }: Props) {
  const { t } = useTranslation('common')

  const tabs: { id: ScreenName; label: string; icon: typeof MessageCircle; size: number }[] = [
    { id: 'chat', label: t('tabs.chat'), icon: MessageCircle, size: 20 },
    { id: 'mail', label: t('tabs.mail'), icon: Mail, size: 20 },
    { id: 'notebook', label: t('tabs.notebook'), icon: BookOpenText, size: 20 },
    { id: 'followups', label: t('tabs.followups'), icon: CircleDot, size: 20 },
    { id: 'settings', label: t('tabs.settings'), icon: Settings, size: 20 },
  ]
  // Briefing, Plugins, Cortex, and Activity are sub-screens of Settings
  const effectiveTab = ['briefing', 'plugins', 'cortex', 'activity'].includes(activeTab) ? 'settings' : activeTab

  return (
    <div className="bg-charcoal border-t border-gold/10 flex items-end justify-evenly shrink-0 pb-[env(safe-area-inset-bottom)]">
      {tabs.map(({ id, label, icon: Icon, size }) => {
        const active = effectiveTab === id
        return (
          <button
            key={id}
            onClick={() => onNavigate(id)}
            className={`relative flex flex-col items-center gap-1 py-2.5 transition-colors ${
              active ? 'text-gold' : 'text-pearl/50'
            }`}
          >
            {active && (
              <motion.div
                layoutId="activeTab"
                className="absolute top-0 inset-x-0 h-0.5 bg-gold"
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              />
            )}
            <Icon
              size={size}
              strokeWidth={active ? 2 : 1.5}
              style={active ? { filter: 'drop-shadow(0 0 6px rgba(212, 168, 83, 0.4))' } : undefined}
            />
            <span className="text-xs font-medium">{label}</span>
          </button>
        )
      })}
    </div>
  )
}
